import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Sparkles, Share2, Camera, Play, Download } from "lucide-react";
import { resolveFileUrl } from "../lib/urls";
import Logo from "../components/Logo";
import ReelPlayer from "../components/ReelPlayer";

const HighlightsPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const base =
      (typeof window !== "undefined" && window.location && window.location.origin) ||
      process.env.REACT_APP_BACKEND_URL;
    axios
      .get(`${base}/api/public/events/${slug}/highlights`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Highlights not available yet."));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    const shareData = {
      title: `${data?.event_name || "Event"} — Highlights Reel`,
      text: `See the best moments from ${data?.event_name || "our event"} on GalerieCop.`,
      url,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch { /* fall through */ }
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const downloadZip = () => {
    // Public endpoint — direct navigation triggers the browser download.
    // Preserves auth cookies for private/paid events automatically.
    const base = (typeof window !== "undefined" && window.location && window.location.origin) || "";
    window.location.href = `${base}/api/public/events/${slug}/highlights/download`;
  };

  if (error) {
    return (
      <div className="min-h-[80vh] bg-cream-100 flex flex-col items-center justify-center px-5 text-center" data-testid="highlights-empty">
        <Camera className="w-10 h-10 text-navy-700/40 mb-3" />
        <h1 className="font-serif italic text-navy-700 text-3xl mb-2">Highlights not ready yet</h1>
        <p className="text-navy-700/60 text-sm max-w-md">{error}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-[80vh] flex items-center justify-center text-navy-700/50" data-testid="highlights-loading">Curating the best moments…</div>;
  }

  const dateLabel = data.event_date
    ? new Date(data.event_date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "";
  const displayTitle = data.reel_title || data.event_name;
  const displaySubtitle = data.reel_subtitle || dateLabel;

  return (
    <div className="min-h-screen bg-cream-100" data-testid="highlights-page">
      {playing && (
        <ReelPlayer
          uploads={data.uploads}
          title={displayTitle}
          subtitle={displaySubtitle}
          onClose={() => setPlaying(false)}
        />
      )}
      {/* Hero cover */}
      <section className="relative overflow-hidden bg-navy-700 text-white">
        {!data.reel_hide_branding && (
          <div className="absolute top-4 left-5 z-10">
            <Logo size="sm" tint="white" className="drop-shadow-lg" />
          </div>
        )}
        {data.uploads[0] && (
          data.uploads[0].kind === "video" ? (
            <video
              src={resolveFileUrl(data.uploads[0].file_url)}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover opacity-40"
              data-testid="highlights-hero-video"
            />
          ) : (
            <img
              src={resolveFileUrl(data.uploads[0].file_url)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-40"
            />
          )
        )}
        <div className="relative max-w-5xl mx-auto px-6 py-24 md:py-32 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Highlights Reel
          </div>
          <h1 className="font-serif italic text-4xl md:text-6xl lg:text-7xl mb-4 leading-tight" data-testid="highlights-title">
            {displayTitle}
          </h1>
          {displaySubtitle && <div className="text-white/80 text-sm md:text-base" data-testid="highlights-date">{displaySubtitle}</div>}
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <button
              onClick={() => setPlaying(true)}
              data-testid="highlights-play-btn"
              className="inline-flex items-center gap-2 bg-coral-300 hover:bg-coral-400 text-white px-6 py-3 rounded-full font-semibold text-base"
            >
              <Play className="w-5 h-5 fill-white" /> Play reel
            </button>
            <button
              onClick={share}
              data-testid="highlights-share-btn"
              className="inline-flex items-center gap-2 bg-white text-navy-700 px-5 py-2.5 rounded-full font-semibold hover:bg-cream-100"
            >
              <Share2 className="w-4 h-4" /> {copied ? "Link copied!" : "Share reel"}
            </button>
            <button
              onClick={downloadZip}
              data-testid="highlights-download-btn"
              title="Download every photo from this reel as a ZIP file. Videos remain available on the shareable reel page."
              className="inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/30 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-white/25"
            >
              <Download className="w-4 h-4" /> Download photos
            </button>
            <span className="text-white/70 text-xs" data-testid="highlights-count">
              {data.count} best moments
            </span>
          </div>
        </div>
      </section>

      {/* Reel grid — staggered masonry-ish */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-16">
        {data.uploads.length === 0 ? (
          <div className="text-center text-navy-700/60 py-20">The reel is empty.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {data.uploads.map((u, idx) => (
              <figure
                key={u.id}
                data-testid={`highlights-item-${idx}`}
                className={`overflow-hidden rounded-2xl bg-white soft-shadow break-inside-avoid ${
                  idx % 7 === 0 ? "md:col-span-2 md:row-span-2" : ""
                }`}
              >
                {u.kind === "video" ? (
                  <video
                    src={resolveFileUrl(u.file_url)}
                    controls
                    preload="metadata"
                    playsInline
                    className="w-full h-full object-cover aspect-[4/3] bg-black"
                    data-testid={`highlights-video-${idx}`}
                  />
                ) : (
                  <img
                    src={resolveFileUrl(u.file_url)}
                    alt={u.uploader_name || "highlight"}
                    loading="lazy"
                    className="w-full h-full object-cover aspect-[4/3] hover:scale-[1.02] transition-transform duration-500"
                  />
                )}                {u.uploader_name && (
                  <figcaption className="px-4 py-2.5 text-xs text-navy-700/60">
                    By <span className="text-navy-700 font-semibold">{u.uploader_name}</span>
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      {!data.reel_hide_branding && (
        <footer className="text-center py-10 text-navy-700/50 text-xs" data-testid="highlights-branding-footer">
          <p>Powered by <span className="font-serif italic text-navy-700">GalerieCop</span></p>
        </footer>
      )}
    </div>
  );
};

export default HighlightsPage;
